import { css } from '@emotion/css';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import { addDays, endOfDay } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setOpenedErrorSurveyModal, setOpenedQuestionnaire } from 'store/questionnaireSlice';
import { RootState } from 'store/store';
import { colors } from 'styles/colors';
import { QuestionnaireAPI, QuestionnaireStatus } from 'utils/QuestionnaireAPI';

import Skeleton from './Skeleton';

import checkMark from 'assets/icons/checkMark.svg';
import rightArrow from 'assets/icons/right-arrow-black.svg';

interface QuestionnaireCardProps extends QuestionnaireStatus {}

const QuestionnaireCard = ({
    questionnaireId,
    title,
    isCompleted,
    completedDate,
    expectedCompletionTime,
}: QuestionnaireCardProps) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const token = useSelector((state: RootState) => state.global.authToken);
    const participantDetails = useSelector((state: RootState) => state.user.participantDetails);
    const isLoading = useSelector((state: RootState) => state.global.isLoading);

    const onOpenQuestionnaire = () => {
        token &&
            QuestionnaireAPI.getQuestionnaire(questionnaireId, token).then((questionnaire) => {
                if (!participantDetails) {
                    return;
                }
                let canOpenQuestionnaire = true;
                if (participantDetails.v4MeetingDate) {
                    const currentDate = new Date();
                    const dayLimit = endOfDay(addDays(new Date(participantDetails.v4MeetingDate), 1));
                    canOpenQuestionnaire = currentDate <= dayLimit;
                }
                if (canOpenQuestionnaire) {
                    dispatch(setOpenedQuestionnaire(questionnaire));
                    navigate('/questionnaire-json');
                } else {
                    dispatch(setOpenedErrorSurveyModal(true));
                }
            });
    };

    const icon = isCompleted ? (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 29 29" className={styles.iconSvg}>
            <image href={checkMark} width="29" height="29" />
        </svg>
    ) : (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 29 29" className={styles.iconSvg}>
            <image href={rightArrow} width="29" height="29" />
        </svg>
    );

    return (
        <div>
            <Card
                className={styles.card}
                style={{
                    backgroundColor: isCompleted ? colors.accent : colors.backgroundLight,
                    cursor: !isCompleted ? 'pointer' : 'default',
                }}
            >
                <CardActionArea
                    onClick={!isCompleted ? onOpenQuestionnaire : undefined}
                    disableRipple
                    aria-label={title}
                    className={styles.actionArea}
                    disabled={!!isCompleted}
                >
                    <CardContent className={styles.cardContent}>
                        <div className={styles.textContainer}>
                            <Skeleton loading={isLoading}>
                                <div className={styles.topText}>{isLoading ? 'loading title' : title}</div>
                            </Skeleton>

                            <Skeleton loading={isLoading}>
                                <div className={styles.bottomText}>
                                    {completedDate
                                        ? new Intl.DateTimeFormat('da-DK', {
                                            day: 'numeric',
                                            month: 'numeric',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                        }).format(new Date(completedDate))
                                        : (expectedCompletionTime ?? 1) + ' min'}
                                </div>
                            </Skeleton>
                        </div>
                        <div className={styles.buttonContainer}>
                            <Skeleton variant="circular" loading={isLoading}>
                                <div className={styles.button}>{icon}</div>
                            </Skeleton>
                        </div>
                    </CardContent>
                </CardActionArea>
            </Card>
        </div>
    );
};

const styles = {
    card: css({
        maxWidth: '26.5rem',
        height: '5.4375rem',
        display: 'flex',
        alignItems: 'center',
        marginBottom: '1.6rem',
        borderRadius: '1rem !important',
        outline: 'none',
        overflow: 'hidden', // clip hover overlay to rounded corners
    }),
    actionArea: css({
        width: '100%',
        height: '100%',
        display: 'block',
        borderRadius: 'inherit',
    }),
    cardContent: css({
        // Unified row layout with no default padding, so hover doesn't change perceived size
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 0,
    }),
    textContainer: css({
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        gap: '0.5rem',
        maxWidth: '15.7675rem',
        marginLeft: '2.25rem',
        '@media (max-width: 910px)': {
            width: '60%',
        },
    }),
    buttonContainer: css({
        marginRight: '2rem',
    }),
    button: css({
        display: 'flex',
        justifyContent: 'flex-end',
    }),
    iconSvg: css({
        width: '29px',
        height: '29px',
        flexShrink: 0,
    }),
    topText: css({
        color: colors.primaryText,
        fontFamily: 'Figtree',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: 'normal',
        overflow: 'hidden',
        display: '-webkit-box',
        '-webkit-line-clamp': '2',
        '-webkit-box-orient': 'vertical',
    }),
    bottomText: css({
        borderTop: '0.6px solid ' + colors.primaryText,
        paddingTop: '0.5rem',
        color: colors.primaryText,
        fontFamily: 'Figtree',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: 'normal',
        opacity: '0.6',
    }),
};

export default QuestionnaireCard;
